/**
 * Lists: the small shared vocabulary — the address a list lives at, the defaults it starts
 * with, and the flag that says the company has them at all.
 *
 * A list's address is /lists/<name>-<id>. The name is for the person reading the address
 * bar and the id is what is looked up, so renaming a list breaks no link anybody has kept:
 * the page reads the id, finds the list, and quietly corrects the address to the new name.
 * The same shape GitHub gives an issue.
 */
const UUID_TAIL = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Keep in step with DEFAULT_COLOR / DEFAULT_ICON in server/src/repos/lists.js.
export const DEFAULT_LIST_COLOR = '#64748b'
export const DEFAULT_LIST_ICON = 'list-tree'

/**
 * The colours offered for a list, as one row of swatches.
 *
 * A palette rather than only the browser's colour dialog, because picking a hex by hand is
 * how you end up with a list nobody can read: every one of these is dark enough for white
 * text and pale enough that the 90% tint it washes its days in stays a wash. The dialog is
 * still there beside them for a company that wants its own.
 *
 * Lower-case six-digit hex, the shape repos/lists.js normalises to and the migration's CHECK
 * holds the column to.
 */
export const LIST_COLORS = [
  '#64748b', '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#16a34a', '#0d9488',
  '#0284c7', '#2563eb', '#4f46e5', '#7c3aed', '#db2777', '#92400e',
]

/** "On-call phone" → "on-call-phone". Accents dropped, anything else non-alphanumeric a hyphen. */
export function slugify(name) {
  const s = String(name || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return s || 'list'
}

export const listPath = (list) => `/lists/${slugify(list.name)}-${list.id}`

/** The id out of a /lists/:slug segment, or null when the segment carries none. */
export function listIdFromParam(param) {
  const m = UUID_TAIL.exec(String(param || ''))
  return m ? m[0].toLowerCase() : null
}

/**
 * `=== true`, like assetsOnBoard and for the same reason: off is the default, so the first
 * paint before /api/me lands must not flash a nav item that is about to disappear.
 */
export const listsEnabled = (lists) => lists?.enabled === true
